import { useEffect, useState } from 'react'

type AudioURL = {
  data: string
  type: string
  file: File | string
}

/** Why the microphone could not start: denied by the learner or the browser, or no capture support at all. */
export type RecorderError = 'blocked' | 'unsupported' | 'no-device' | null

// MediaRecorder picks its own container: Chrome and Firefox record WebM/Opus,
// Safari records MP4/AAC. We keep whatever it chose instead of relabelling the
// bytes as MP3, so uploads are signed and named for the real format.
const extensionForMime = (mime: string): string => {
  const base = (mime || '').split(';')[0].trim().toLowerCase()
  if (base === 'audio/webm' || base === 'video/webm') return 'webm'
  if (base === 'audio/ogg') return 'ogg'
  if (base === 'audio/mp4' || base === 'video/mp4' || base === 'audio/m4a' || base === 'audio/x-m4a') return 'm4a'
  if (base === 'audio/mpeg') return 'mp3'
  if (base === 'audio/wav' || base === 'audio/wave') return 'wav'
  return 'webm'
}

/**
 * The content type to sign an upload for. The upload-url endpoint accepts
 * audio/m4a, audio/mp4, audio/webm, audio/ogg and audio/mpeg, without codec
 * parameters.
 */
export const uploadContentTypeFor = (mime: string): string => {
  const base = (mime || '').split(';')[0].trim().toLowerCase()
  if (base === 'video/webm' || base === 'audio/webm') return 'audio/webm'
  if (base === 'video/mp4' || base === 'audio/mp4' || base === 'audio/x-m4a') return 'audio/mp4'
  if (base === 'audio/m4a') return 'audio/m4a'
  if (base === 'audio/ogg') return 'audio/ogg'
  if (base === 'audio/mpeg') return 'audio/mpeg'
  return 'audio/webm'
}

const classifyError = (err: any): RecorderError => {
  const name = err?.name || ''
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return 'blocked'
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'no-device'
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'no-device'
  return 'unsupported'
}

const useRecorder = () => {
  const [audioURL, setAudioURL] = useState<AudioURL>({
    data: '',
    type: '',
    file: '',
  })
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null)
  const [isRecordingReset, setIsRecordingReset] = useState<boolean>(false)
  const [error, setError] = useState<RecorderError>(null)

  useEffect(() => {
    // Lazily obtain recorder first time we're recording.
    if (recorder === null) {
      if (isRecording) {
        requestRecorder().then(
          (rec: MediaRecorder) => {
            setError(null)
            setRecorder(rec)
          },
          (err) => {
            console.error(err)
            // Without this the UI would sit in "recording" forever with no stream.
            setError(classifyError(err))
            setIsRecording(false)
          },
        )
      }
      return
    }
    // Manage recorder state.
    if (isRecording) {
      if (recorder.state === 'inactive') recorder.start()
    } else if (recorder.state !== 'inactive') {
      recorder.stop()
    } else {
      // Stopped before the microphone finished opening: nothing was recorded,
      // so just release the device.
      recorder.stream?.getTracks().forEach((track) => track.stop())
      setRecorder(null)
      return
    }
    // Obtain the audio when ready.
    const handleData = (e: BlobEvent) => {
      const mime = e.data?.type || recorder.mimeType || 'audio/webm'
      const d = new Date()
      const file = new File([e.data], `${d.valueOf()}.${extensionForMime(mime)}`, { type: mime })
      const tracks = recorder?.stream?.getTracks()
      tracks?.forEach((track) => {
        track.stop()
        return track
      })
      if (isRecordingReset) {
        setAudioURL({
          data: '',
          type: '',
          file: '',
        })
      } else {
        setAudioURL({
          data: URL.createObjectURL(e.data),
          type: mime,
          file,
        })
      }
      setRecorder(null)
    }
    recorder.addEventListener('dataavailable', handleData)
    return () => recorder.removeEventListener('dataavailable', handleData)
  }, [recorder, isRecording])
  const startRecording = () => {
    setError(null)
    setIsRecordingReset(false)
    setIsRecording(true)
  }
  const stopRecording = (recordingReset: boolean) => {
    setIsRecordingReset(recordingReset)
    setIsRecording(false)
  }
  const clearError = () => setError(null)
  // mediaRecorder is exposed so the UI can tap the SAME getUserMedia stream for
  // analysis (live waveform, live transcription) without opening a second
  // capture stream. It is only non-null while a recording session is active.
  return { audioURL, isRecording, startRecording, stopRecording, mediaRecorder: recorder, error, clearError }
}

async function requestRecorder(): Promise<MediaRecorder> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.getUserMedia ||
    typeof MediaRecorder === 'undefined'
  ) {
    const err: any = new Error('Recording is not supported in this browser')
    err.name = 'NotSupportedError'
    throw err
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  return new MediaRecorder(stream)
}

export default useRecorder
